import { useContext, useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router";

import { Button, Message, Messages, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Heading, Regular } from "capitalrx-components-2.0/lib/typography";

import { getProviderManagementNetworkConfigListAPI, getProviderManagementNetworkListAPI } from "@/api";
import { ProviderManagement } from "@/api/interfaces";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { getThemeButton, getThemeURL, transformNetworkData } from "@/utils";

import { NetworkTable } from "./NetworkTable";

type NetworkListProps = {
  readonly provider: ProviderManagement.ProviderSchema;
};

export const NetworkList: FC<NetworkListProps> = ({ provider }) => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const { primary_color, secondary_hover_color } = useContext(ThemeSelectorContext);
  const navigate = useNavigate();
  const [networkResults, setNetworkResults] = useState<ProviderManagement.ProviderNetworkListSearchResponse>();
  const [isNetworkLoading, setIsNetworkLoading] = useState(false);
  const [networkConfigResults, setNetworkConfigResults] = useState<ProviderManagement.ProviderNetworkConfigListSearchResponse>();
  const [isNetworkConfigLoading, setIsNetworkConfigLoading] = useState(false);

  useEffect(() => {
    const fetchNetworks = async () => {
      setIsNetworkLoading(true);
      try {
        const requestPayload = { page_number: 1, results_per_page: 10, order_by: [] };
        const response = await getProviderManagementNetworkListAPI(requestPayload, getAuthToken());
        setNetworkResults(response);
      } catch (error) {
        console.error("Failed to fetch networks:", error);
      } finally {
        setIsNetworkLoading(false);
      }
    };
    const fetchNetworkConfigs = async () => {
      setIsNetworkConfigLoading(true);
      try {
        const requestPayload = { provider_id: provider.id };
        const response = await getProviderManagementNetworkConfigListAPI(requestPayload, getAuthToken());
        setNetworkConfigResults(response);
      } catch (error) {
        console.error("Failed to fetch network configurations:", error);
      } finally {
        setIsNetworkConfigLoading(false);
      }
    };

    fetchNetworks();
    fetchNetworkConfigs();
  }, [getAuthToken, provider]);


  const handleNewNetwork = () => {
    navigate(getThemeURL("ACCOUNT_NETWORK_CREATE"));
  };

  const networkInfo = transformNetworkData(networkResults);

  const configuredNetworkIds = new Set(
    networkConfigResults?.results?.map((result) => result.provider_network_configuration.provider_network_id) || [],
  );

  const remainingNetworks =
    networkInfo ? networkInfo.filter((network) => !configuredNetworkIds.has(network?.id) && network?.in_network !== false) : undefined;

  const isAvailable = remainingNetworks ? remainingNetworks?.length > 0 : false;


  return (
    <Stack spacing="large">
      {(isNetworkConfigLoading || isNetworkLoading) && (
        <Stack alignment="center"><Spinner size="large" /></Stack>
      )}
      {!isNetworkConfigLoading && !isNetworkLoading && (
        <>
          <Stack spacing="none" alignment="left">
            <Shelf>
              <Heading>Networks</Heading>
              <Button icon="add" appearance={getThemeButton("tertiary", primary_color, secondary_hover_color)} onClick={handleNewNetwork} disabled={!isAvailable}>
                New Network
              </Button>
            </Shelf>
          </Stack>
          <Message visible={!isAvailable} appearance={Messages.Warning}>
            <Regular>All possible network preferences have been added</Regular>
          </Message>
          <NetworkTable provider={provider} networks={networkResults} config={networkConfigResults} />
        </>
      )}
    </Stack>
  );
};
